/**
 * PhotoLightbox — Iter-290b Sprint B
 *
 * Full-screen, mobile-friendly photo viewer with prev/next, swipe, ESC + click
 * outside to close. Reused by the public profile + portfolio thumbnails.
 *
 * Props:
 *   open          : boolean
 *   images        : string[]  (full URLs or "/api/..." paths)
 *   index         : initial index
 *   onClose       : () => void
 *   resolveSrc?   : (src) => string  (defaults to identity)
 *   alt?          : string  (accessibility alt)
 *
 * Keyboard:  ←/→ navigate, ESC closes
 * Touch:     swipe-left/right navigates, tap on backdrop closes
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

const SWIPE_THRESHOLD_PX = 50;

export default function PhotoLightbox({
  open,
  images,
  index = 0,
  onClose,
  resolveSrc = (s) => s,
  alt = 'Photo',
}) {
  const [current, setCurrent] = useState(index);
  const touchStartX = useRef(null);

  useEffect(() => { if (open) setCurrent(Math.max(0, Math.min(index, (images?.length || 1) - 1))); }, [open, index, images]);

  const count = Array.isArray(images) ? images.length : 0;

  const goPrev = useCallback(() => setCurrent((i) => (count ? (i - 1 + count) % count : 0)), [count]);
  const goNext = useCallback(() => setCurrent((i) => (count ? (i + 1) % count : 0)), [count]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    document.addEventListener('keydown', onKey);
    const origOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = origOverflow;
    };
  }, [open, goPrev, goNext, onClose]);

  if (!open || !count) return null;

  const src = resolveSrc(images[current]);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD_PX) {
      if (dx > 0) goPrev(); else goNext();
    }
    touchStartX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none"
      role="dialog"
      aria-modal="true"
      data-testid="photo-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Close */}
      <button
        type="button"
        className="absolute top-4 right-4 p-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white"
        onClick={onClose}
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      {count > 1 && (
        <div
          className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-medium"
          data-testid="lightbox-counter"
        >
          {current + 1} / {count}
        </div>
      )}

      {/* Image */}
      <img
        src={src}
        alt={alt}
        className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-2xl"
        draggable={false}
        data-testid="lightbox-image"
      />

      {/* Prev / Next */}
      {count > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Previous photo"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-2.5 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Next photo"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </>
      )}
    </div>
  );
}
